<template>
  <Listbox v-slot="{ open }" v-model="selectedValues" :disabled="disabled">
    <div class="group relative">
      <template v-if="label">
        <BaseLabelTop
          :target="name"
          :label="label"
          :disabled="disabled"
          :required="required"
        />
      </template>
      <ListboxButton
        data-testid="status-listbox-button"
        class="relative w-full cursor-default rounded-md border bg-white py-1 text-left transition-all sm:text-sm dark:border-slate-300 dark:bg-slate-700 dark:text-white"
        :class="[
          open ? 'border-slate-300 pl-2.5' : '',
          !open && disabled && !label ? 'border-transparent pl-1.5' : '',
          !open && disabled && label ? 'border-slate-300 pl-2.5' : '',
          !open && !disabled && label ? 'border-slate-300 pl-2.5' : '',
          !open && !disabled && !label
            ? 'border-transparent pl-1.5 group-hover:border-slate-300 group-hover:pl-2.5'
            : ''
        ]"
      >
        <div class="flex flex-row items-center gap-2">
          <span
            :data-tint="themeColors ? null : selectedColor"
            class="h-3 w-3 flex-shrink-0 rounded-sm"
            :class="swatchClass(selectedColor)"
          >
            &nbsp;
          </span>
          <span
            :class="[
              selected ? '' : 'text-gray-500',
              'flex-1 truncate text-left'
            ]"
          >
            {{ selectedValuesLabel }}
          </span>
          <span
            class="flex-shrink-0 px-2 text-gray-500 opacity-0"
            :class="[
              open ? 'opacity-100' : '',
              disabled ? '' : 'group-hover:opacity-100'
            ]"
          >
            <FontAwesomeIcon :icon="['fal', 'chevron-down']" />
          </span>
        </div>
      </ListboxButton>

      <transition
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ListboxOptions
          class="absolute z-90 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm dark:border dark:border-white dark:bg-slate-700 dark:text-white"
        >
          <ListboxOption
            v-for="(status, index) in options"
            :key="index"
            v-slot="{ active, selected }"
            :value="status.id"
            as="template"
          >
            <li
              :data-testid="`status-option-${status.id}`"
              :class="[
                active ? 'bg-primary-300 text-white' : 'text-gray-900',
                'relative cursor-default px-2 py-1 select-none dark:text-white'
              ]"
            >
              <div class="flex flex-row items-center gap-2">
                <span
                  :data-tint="themeColors ? null : status.bg_color"
                  class="h-3 w-3 rounded-sm"
                  :class="swatchClass(status.bg_color)"
                >
                  &nbsp;
                </span>
                <span
                  :class="[
                    selected ? 'font-medium' : 'font-normal',
                    'block truncate'
                  ]"
                >
                  {{ status.name.toLocaleLowerCase() }}
                </span>
              </div>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </div>
  </Listbox>
  <div
    v-for="(error, index) in errors"
    :key="index"
    class="form-help text-red-600"
  >
    {{ $t(error, { attribute: $t('attributes.' + name) }) }}
  </div>
</template>

<script>
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption
} from '@headlessui/vue'
import BaseEditLabel from '@c/addf-package/components/BaseLabel/BaseEditLabel'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseLabelTop from '@c/addf-package/components/BaseLabel/BaseLabelTop.vue'

export default {
  name: 'StatusInput',
  components: {
    BaseLabelTop,
    FontAwesomeIcon,
    BaseEditLabel,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption
  },
  props: {
    modelValue: {
      type: String,
      required: false,
      default: null
    },
    options: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    label: {
      type: String,
      required: false,
      default: null
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    name: {
      type: String,
      required: false,
      default: null
    },
    errors: {
      type: Array,
      required: false,
      default: null
    },
    // Crema: `bg_color` is a `bg-theme-*` token (work statuses), not a tint.
    themeColors: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:modelValue', 'status-changed', 'change'],
  methods: {
    swatchClass(color) {
      return this.themeColors
        ? `bg-theme-${color}`
        : 'bg-gradient-to-br from-primary-400 to-primary-600'
    }
  },
  computed: {
    selectedValuesLabel() {
      return (
        this.options
          .find((status) => status.id === this.modelValue)
          ?.name.toLocaleLowerCase() ?? this.$t('global.select')
      )
    },
    selected() {
      return this.options.find((option) => option.id === this.modelValue)
    },
    selectedColor() {
      return this.options.find((status) => status.id === this.modelValue)
        ?.bg_color
    },
    selectedValues: {
      get() {
        return this.modelValue
      },
      set(newValue) {
        this.$emit('update:modelValue', newValue)
        this.$emit('status-changed', newValue)
        this.$emit('change', newValue)
      }
    }
  }
}
</script>
